import React, { memo, useState } from 'react';
import { shallow } from 'zustand/shallow';
import useSimulationStore from '../../../store/simulationStore';
import {
    unifiedControlsContainer,
    unifiedControlSection,
    unifiedControlSectionHeader,
    unifiedControlSectionIcon,
    unifiedControlSectionContent,
    unifiedControlGroup,
    unifiedControlButton,
    unifiedControlButtonStart,
    unifiedControlButtonPause,
    unifiedControlButtonReset,
    unifiedControlLabel,
    unifiedControlSlider,
    unifiedControlToggleLabel,
    unifiedControlCheckbox,
    unifiedControlSelect,
    unifiedControlColorPicker,
} from '../unifiedPanelStyles';

const UnifiedControlPanel: React.FC = memo(() => {
    const {
        controls,
        setControls,
        isRunning,
        startSimulation,
        pauseSimulation,
        resetSimulation,
        // Display/Rendering settings
        metalness,
        roughness,
        emissiveIntensity,
        bloomIntensity,
        bloomThreshold,
        bloomRadius,
        boxStyle,
        setMetalness,
        setRoughness,
        setEmissiveIntensity,
        setBloomIntensity,
        setBloomThreshold,
        setBloomRadius,
        setBoxStyle,
        // View settings
        cameraFov,
        backgroundColor,
        ambientLightIntensity,
        viewportScaleFactor,
        setCameraFov,
        setBackgroundColor,
        setAmbientLightIntensity,
        setViewportScaleFactor
    } = useSimulationStore(
        state => ({
            controls: state.controls,
            setControls: state.setControls,
            isRunning: state.isRunning,
            startSimulation: state.startSimulation,
            pauseSimulation: state.pauseSimulation,
            resetSimulation: state.resetSimulation,
            metalness: state.metalness,
            roughness: state.roughness,
            emissiveIntensity: state.emissiveIntensity,
            bloomIntensity: state.bloomIntensity,
            bloomThreshold: state.bloomThreshold,
            bloomRadius: state.bloomRadius,
            boxStyle: state.boxStyle,
            setMetalness: state.setMetalness,
            setRoughness: state.setRoughness,
            setEmissiveIntensity: state.setEmissiveIntensity,
            setBloomIntensity: state.setBloomIntensity,
            setBloomThreshold: state.setBloomThreshold,
            setBloomRadius: state.setBloomRadius,
            setBoxStyle: state.setBoxStyle,
            cameraFov: state.cameraFov,
            backgroundColor: state.backgroundColor,
            ambientLightIntensity: state.ambientLightIntensity,
            viewportScaleFactor: state.viewportScaleFactor,
            setCameraFov: state.setCameraFov,
            setBackgroundColor: state.setBackgroundColor,
            setAmbientLightIntensity: state.setAmbientLightIntensity,
            setViewportScaleFactor: state.setViewportScaleFactor
        }),
        shallow
    );

    const [expandedSections, setExpandedSections] = useState({
        basic: true,
        physics: false,
        boxDimensions: false,
        display: false,
        rendering: false,
        view: false
    });

    const toggleSection = (section: keyof typeof expandedSections) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    return (
        <div style={unifiedControlsContainer}>
            {/* Basic Controls */}
            <div style={unifiedControlSection}>
                <button
                    style={unifiedControlSectionHeader}
                    onClick={() => toggleSection('basic')}
                >
                    <span style={unifiedControlSectionIcon}>
                        {expandedSections.basic ? '▼' : '▶'}
                    </span>
                    Basic Controls
                </button>
                {expandedSections.basic && (
                    <div style={unifiedControlSectionContent}>
                        <div style={unifiedControlGroup}>
                            <button
                                style={{
                                    ...unifiedControlButton,
                                    ...(isRunning ? unifiedControlButtonPause : unifiedControlButtonStart)
                                }}
                                onClick={isRunning ? pauseSimulation : startSimulation}
                            >
                                {isRunning ? '⏸️ Pause' : '▶️ Start'}
                            </button>
                            <button
                                style={unifiedControlButtonReset}
                                onClick={resetSimulation}
                            >
                                🔄 Reset
                            </button>
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Speed: {controls.simulationSpeed.toFixed(2)}</label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={controls.simulationSpeed}
                                onChange={(e) => setControls({ simulationSpeed: parseFloat(e.target.value) })}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Size: {controls.reactorSize}</label>
                            <input
                                type="range"
                                min="10"
                                max="150"
                                step="10"
                                value={controls.reactorSize}
                                onChange={(e) => setControls({ reactorSize: parseInt(e.target.value) })}
                                style={unifiedControlSlider}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Physics Controls */}
            <div style={unifiedControlSection}>
                <button
                    style={unifiedControlSectionHeader}
                    onClick={() => toggleSection('physics')}
                >
                    <span style={unifiedControlSectionIcon}>
                        {expandedSections.physics ? '▼' : '▶'}
                    </span>
                    Physics
                </button>
                {expandedSections.physics && (
                    <div style={unifiedControlSectionContent}>
                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Elasticity: {controls.collisionElasticity.toFixed(2)}</label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={controls.collisionElasticity}
                                onChange={(e) => setControls({ collisionElasticity: parseFloat(e.target.value) })}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Particle Speed: {controls.particleSpeed.toFixed(2)}</label>
                            <input
                                type="range"
                                min="0"
                                max="10"
                                step="0.1"
                                value={controls.particleSpeed}
                                onChange={(e) => setControls({ particleSpeed: parseFloat(e.target.value) })}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlToggleLabel}>
                                <input
                                    type="checkbox"
                                    checked={controls.is2DMode}
                                    onChange={(e) => setControls({ is2DMode: e.target.checked })}
                                    style={unifiedControlCheckbox}
                                />
                                2D Mode (XY Plane)
                            </label>
                        </div>
                    </div>
                )}
            </div>

            {/* Box Dimensions Controls */}
            <div style={unifiedControlSection}>
                <button
                    style={unifiedControlSectionHeader}
                    onClick={() => toggleSection('boxDimensions')}
                >
                    <span style={unifiedControlSectionIcon}>
                        {expandedSections.boxDimensions ? '▼' : '▶'}
                    </span>
                    Box Dimensions
                </button>
                {expandedSections.boxDimensions && (
                    <div style={unifiedControlSectionContent}>
                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Width: {controls.reactorWidth || controls.reactorSize}</label>
                            <input
                                type="range"
                                min="5"
                                max="50"
                                step="1"
                                value={controls.reactorWidth || controls.reactorSize}
                                onChange={(e) => setControls({ reactorWidth: parseInt(e.target.value) })}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Height: {controls.reactorHeight || controls.reactorSize}</label>
                            <input
                                type="range"
                                min="5"
                                max="50"
                                step="1"
                                value={controls.reactorHeight || controls.reactorSize}
                                onChange={(e) => setControls({ reactorHeight: parseInt(e.target.value) })}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Depth: {controls.reactorDepth || controls.reactorSize}</label>
                            <input
                                type="range"
                                min="1"
                                max="20"
                                step="0.5"
                                value={controls.reactorDepth || controls.reactorSize}
                                onChange={(e) => setControls({ reactorDepth: parseFloat(e.target.value) })}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <button
                                style={unifiedControlButton}
                                onClick={() => {
                                    // Reset to default dimensions
                                    setControls({
                                        reactorWidth: 10,
                                        reactorHeight: 10,
                                        reactorDepth: 10,
                                        reactorSize: 10
                                    });
                                }}
                            >
                                🔄 Reset to Default
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Display Controls */}
            <div style={unifiedControlSection}>
                <button
                    style={unifiedControlSectionHeader}
                    onClick={() => toggleSection('display')}
                >
                    <span style={unifiedControlSectionIcon}>
                        {expandedSections.display ? '▼' : '▶'}
                    </span>
                    Display
                </button>
                {expandedSections.display && (
                    <div style={unifiedControlSectionContent}>
                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Particle Scale: {controls.particleScale.toFixed(2)}</label>
                            <input
                                type="range"
                                min="0.1"
                                max="3"
                                step="0.1"
                                value={controls.particleScale}
                                onChange={(e) => setControls({ particleScale: parseFloat(e.target.value) })}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Tiling: {controls.tilingFactor}</label>
                            <input
                                type="range"
                                min="1"
                                max="10"
                                step="1"
                                value={controls.tilingFactor}
                                onChange={(e) => setControls({ tilingFactor: parseInt(e.target.value) })}
                                style={unifiedControlSlider}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Rendering Controls */}
            <div style={unifiedControlSection}>
                <button
                    style={unifiedControlSectionHeader}
                    onClick={() => toggleSection('rendering')}
                >
                    <span style={unifiedControlSectionIcon}>
                        {expandedSections.rendering ? '▼' : '▶'}
                    </span>
                    Rendering
                </button>
                {expandedSections.rendering && (
                    <div style={unifiedControlSectionContent}>
                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Metalness: {metalness.toFixed(2)}</label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={metalness}
                                onChange={(e) => setMetalness(parseFloat(e.target.value))}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Roughness: {roughness.toFixed(2)}</label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={roughness}
                                onChange={(e) => setRoughness(parseFloat(e.target.value))}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Emissive: {emissiveIntensity.toFixed(2)}</label>
                            <input
                                type="range"
                                min="0"
                                max="2"
                                step="0.1"
                                value={emissiveIntensity}
                                onChange={(e) => setEmissiveIntensity(parseFloat(e.target.value))}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Bloom Intensity: {bloomIntensity.toFixed(2)}</label>
                            <input
                                type="range"
                                min="0"
                                max="3"
                                step="0.1"
                                value={bloomIntensity}
                                onChange={(e) => setBloomIntensity(parseFloat(e.target.value))}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Bloom Threshold: {bloomThreshold.toFixed(2)}</label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={bloomThreshold}
                                onChange={(e) => setBloomThreshold(parseFloat(e.target.value))}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Bloom Radius: {bloomRadius.toFixed(2)}</label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={bloomRadius}
                                onChange={(e) => setBloomRadius(parseFloat(e.target.value))}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Box Style:</label>
                            <select
                                value={boxStyle}
                                onChange={(e) => setBoxStyle(e.target.value as any)}
                                style={unifiedControlSelect}
                            >
                                <option value="normal">Normal</option>
                                <option value="hidden">Hidden</option>
                                <option value="glass">Glass</option>
                                <option value="forcefield">Force Field</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* View Controls */}
            <div style={unifiedControlSection}>
                <button
                    style={unifiedControlSectionHeader}
                    onClick={() => toggleSection('view')}
                >
                    <span style={unifiedControlSectionIcon}>
                        {expandedSections.view ? '▼' : '▶'}
                    </span>
                    View
                </button>
                {expandedSections.view && (
                    <div style={unifiedControlSectionContent}>
                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Camera FOV: {cameraFov.toFixed(0)}°</label>
                            <input
                                type="range"
                                min="30"
                                max="120"
                                step="5"
                                value={cameraFov}
                                onChange={(e) => setCameraFov(parseFloat(e.target.value))}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Background:</label>
                            <input
                                type="color"
                                value={backgroundColor}
                                onChange={(e) => setBackgroundColor(e.target.value)}
                                style={unifiedControlColorPicker}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Ambient Light: {ambientLightIntensity.toFixed(2)}</label>
                            <input
                                type="range"
                                min="0"
                                max="2"
                                step="0.1"
                                value={ambientLightIntensity}
                                onChange={(e) => setAmbientLightIntensity(parseFloat(e.target.value))}
                                style={unifiedControlSlider}
                            />
                        </div>

                        <div style={unifiedControlGroup}>
                            <label style={unifiedControlLabel}>Viewport Scale: {viewportScaleFactor.toFixed(1)}</label>
                            <input
                                type="range"
                                min="1"
                                max="5"
                                step="0.1"
                                value={viewportScaleFactor}
                                onChange={(e) => setViewportScaleFactor(parseFloat(e.target.value))}
                                style={unifiedControlSlider}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

UnifiedControlPanel.displayName = 'UnifiedControlPanel';

export default UnifiedControlPanel;
